// src/components/media/LabelPicker.tsx — "what does this photo show?": pick from the list (GET /media/labels) or type
// a new one, which the server remembers and offers from then on (docs/media-library.md, "Photo labels"). There is no
// settings screen for labels; this picker is the only place they are made.
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import { mediaApi } from '../../lib/media';

export function useMediaLabels() {
  return useQuery({ queryKey: ['media', 'labels'], queryFn: mediaApi.labels, staleTime: 5 * 60_000 });
}

/**
 * A combobox over the label list. `value` is the label (or '' for none); a typed label is committed on Enter, on
 * picking "Use …", or when the field loses focus.
 */
export function LabelPicker({ value, onChange, placeholder = 'What is it? Bedroom, Kitchen…', disabled, className = '', autoFocus, size = 'md' }: {
  value: string; onChange: (label: string) => void; placeholder?: string; disabled?: boolean; className?: string; autoFocus?: boolean; size?: 'sm' | 'md';
}) {
  const labels = useMediaLabels();
  const listId = useId();
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const [text, setText] = useState(value);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [pos, setPos] = useState<{ top: number; left: number; width: number; maxHeight: number } | null>(null);

  // The value changed from outside ("apply to all"): show it.
  const [shown, setShown] = useState(value);
  if (shown !== value) { setShown(value); setText(value); }

  const q = text.trim().toLowerCase();
  const names = useMemo(() => (labels.data ?? []).map((l) => l.name), [labels.data]);
  const matches = useMemo(() => (q && q !== value.toLowerCase() ? names.filter((n) => n.toLowerCase().includes(q)) : names), [names, q, value]);
  const exact = names.some((n) => n.toLowerCase() === q);
  const options: Array<{ name: string; isNew: boolean }> = [...matches.map((name) => ({ name, isNew: false })), ...(q && !exact ? [{ name: text.trim().replace(/\s+/g, ' '), isNew: true }] : [])];

  const commit = (next: string) => {
    const clean = next.replace(/\s+/g, ' ').trim().slice(0, 60);
    // The list's own spelling when it is already there ("bedroom" → "Bedroom"); the server does the same.
    const known = names.find((n) => n.toLowerCase() === clean.toLowerCase());
    const final = known ?? clean;
    setText(final); setShown(final); setOpen(false); setActive(-1);
    if (final !== value) onChange(final);
  };

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const r = input.current?.getBoundingClientRect();
      if (!r) return;
      const width = Math.min(Math.max(r.width, 220), window.innerWidth - 16);
      const left = Math.max(8, Math.min(r.left, window.innerWidth - width - 8));
      const below = window.innerHeight - r.bottom - 8; const above = r.top - 8;
      const up = below < 200 && above > below;
      const maxHeight = Math.min(260, up ? above : below);
      setPos({ top: up ? r.top - 4 - maxHeight : r.bottom + 4, left, width, maxHeight });
    };
    place();
    window.addEventListener('resize', place); window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!input.current?.contains(t) && !list.current?.contains(t)) commit(text);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  });

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      if (!options.length) return;
      setActive((i) => (e.key === 'ArrowDown' ? (i + 1) % options.length : i <= 0 ? options.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const o = active >= 0 ? options[active] : undefined;
      commit(o ? o.name : text);
    } else if (e.key === 'Escape') {
      if (!open) return;
      e.preventDefault(); e.stopPropagation(); e.nativeEvent.stopImmediatePropagation();
      setText(value); setOpen(false); setActive(-1);
    } else if (e.key === 'Tab') {
      if (open) commit(active >= 0 && options[active] ? options[active].name : text);
    }
  };

  const h = size === 'sm' ? 'h-7 text-[12px]' : 'h-8 text-[12.5px]';
  return (
    <div className={`relative ${className}`}>
      <input ref={input} type="text" role="combobox" aria-expanded={open} aria-controls={listId} aria-autocomplete="list" aria-label="Label"
        autoComplete="off" autoFocus={autoFocus} disabled={disabled} placeholder={placeholder} value={text}
        onChange={(e) => { setText(e.target.value); setOpen(true); setActive(-1); }}
        onFocus={() => setOpen(true)} onClick={() => setOpen(true)} onKeyDown={onKeyDown}
        className={`${h} w-full rounded-lg border border-ink-200 bg-white px-2.5 pr-7 text-ink-900 placeholder:text-ink-400 outline-none transition focus:border-bronze-500 focus:ring-2 focus:ring-bronze-500/30 disabled:bg-ink-50 disabled:text-ink-400`} />
      {text && !disabled && (
        <button type="button" tabIndex={-1} aria-label="Clear label" onMouseDown={(e) => e.preventDefault()} onClick={() => commit('')}
          className="absolute right-1 top-1/2 -translate-y-1/2 rounded-md p-1 text-ink-400 transition hover:bg-ink-100 hover:text-ink-700">
          <X className="h-3 w-3" />
        </button>
      )}
      {open && pos && createPortal(
        <ul ref={list} id={listId} role="listbox" aria-label="Labels"
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, maxHeight: pos.maxHeight }}
          className="z-[200] overflow-y-auto rounded-lg border border-ink-200 bg-white py-1 shadow-float">
          {labels.isLoading && <li role="presentation" className="px-3 py-1.5 text-[12px] text-ink-400">Loading the list…</li>}
          {options.map((o, i) => (
            <li key={`${o.isNew}-${o.name}`} role="option" aria-selected={i === active}
              onMouseDown={(e) => e.preventDefault()} onMouseEnter={() => setActive(i)} onClick={() => commit(o.name)}
              className={`flex cursor-pointer items-center gap-2 px-3 py-1.5 text-[12.5px] ${o.isNew ? 'border-t border-ink-100 text-bronze-700' : ''} ${i === active ? 'bg-ink-100 text-ink-900' : o.isNew ? '' : 'text-ink-800'}`}>
              {o.isNew ? <><Plus className="h-3.5 w-3.5" />Use “{o.name}” <span className="ml-auto text-[11px] text-ink-400">added to the list</span></> : o.name}
            </li>
          ))}
          {!labels.isLoading && !options.length && <li role="presentation" className="px-3 py-1.5 text-[12px] text-ink-400">Type a label</li>}
        </ul>,
        document.body,
      )}
    </div>
  );
}
